/**
 * TourSpotlight — when the current step has a `targetId`, scroll the matching
 * `[data-tour-id=...]` element into view and draw a glowing ring around it.
 *
 * Fallback chain when the step has no `targetId` (or the anchor never mounts):
 *   1. The current chapter's `navAnchor` (`[data-tour-id="..."]`) — usually a
 *      sidebar group or primary nav item. This gives a stable "you are here"
 *      cue for narration-only steps.
 *   2. The active Books rail link (for accounting chapters).
 *   3. The Books rail container itself.
 *
 * Route-change handling: when the user navigates between steps, the poll
 * counter resets and the search window extends to ~2s so slow routes have
 * time to mount their anchor before we give up and fall back.
 */
import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useAccountingTour } from "@/data/accounting/tour/TourContext";
import { getStepByIndex } from "@/data/accounting/tour/tourChapters";

interface Rect { top: number; left: number; width: number; height: number; }

const MAX_POLL_FRAMES = 120; // ~2s at 60fps

export function TourSpotlight() {
  const tour = useAccountingTour();
  const resolved = useMemo(() => getStepByIndex(tour.currentIndex), [tour.currentIndex]);
  const targetId = resolved?.step.targetId;
  const navAnchor = resolved?.chapter.navAnchor;
  const [rect, setRect] = useState<Rect | null>(null);

  useEffect(() => {
    if (!tour.active) { setRect(null); return; }
    let raf = 0; let tries = 0; let cancelled = false;
    let cleanup: (() => void) | null = null;

    const track = (el: HTMLElement) => {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      const update = () => {
        const r = el.getBoundingClientRect();
        setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
      };
      update();
      const onScroll = () => update();
      window.addEventListener("scroll", onScroll, true);
      window.addEventListener("resize", update);
      const interval = window.setInterval(update, 500);
      cleanup = () => {
        window.removeEventListener("scroll", onScroll, true);
        window.removeEventListener("resize", update);
        window.clearInterval(interval);
      };
    };

    const findFallback = (): HTMLElement | null => {
      if (navAnchor) {
        const el = document.querySelector<HTMLElement>(`[data-tour-id="${navAnchor}"]`);
        if (el) return el;
      }
      // Books rail fallbacks (accounting chapters).
      return (
        document.querySelector<HTMLElement>(
          '[data-books-rail] a[aria-current="page"], [data-books-rail] a.bg-primary\\/15',
        ) ?? document.querySelector<HTMLElement>('[data-books-rail]')
      );
    };

    const findAndTrack = () => {
      if (cancelled) return;
      if (targetId) {
        const el = document.querySelector<HTMLElement>(`[data-tour-id="${targetId}"]`);
        if (el) { track(el); return; }
        if (tries++ < MAX_POLL_FRAMES) { raf = requestAnimationFrame(findAndTrack); return; }
        // Window expired — fall through to fallback.
      }
      const fb = findFallback();
      if (fb) track(fb);
      else setRect(null);
    };
    findAndTrack();

    return () => {
      cancelled = true;
      cancelAnimationFrame(raf);
      if (cleanup) cleanup();
    };
    // Re-run whenever the step or its anchors change. `tour.currentIndex` is
    // the primary trigger; including pathname makes navigation transitions
    // reset the poll cleanly even if currentIndex didn't change.
  }, [tour.active, targetId, navAnchor, tour.currentIndex]);

  if (!tour.active || !rect) return null;

  // Adaptive padding: tiny targets get a thicker halo; full sidebar groups
  // already have generous bounds.
  const isSmall = rect.height < 28 || rect.width < 80;
  const PAD = isSmall ? 14 : 8;
  return createPortal(
    <div
      className="pointer-events-none fixed z-[90] rounded-md ring-2 ring-primary ring-offset-2 ring-offset-background/0 shadow-[0_0_0_9999px_rgba(0,0,0,0.45)] transition-all duration-300"
      style={{
        top: rect.top - PAD,
        left: rect.left - PAD,
        width: rect.width + PAD * 2,
        height: rect.height + PAD * 2,
      }}
    />,
    document.body
  );
}
